import React, { useMemo, useState, useEffect } from "react";
import { useFormikContext } from "formik";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import getBillingLabel from "../../../../utils/billing/getBillingLabel";
import { categories } from "./constants";

export default function RateMultiSelectModal({
  onClose,
  categoryKey,
  rates = {},
  initialSelected = [],
}) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set(initialSelected));
  const title = categories.find(c => c.key === categoryKey)?.title;
  const { values, setValues } = useFormikContext();

  useEffect(() => {
    setSelected(new Set(initialSelected));
  }, [initialSelected, categoryKey]);

  const mapCategoryToSection = key => {
    switch (key) {
      case "materialRates":
        return "materialCharges";
      case "laborRates":
        return "laborCharges";
      case "openInspectRates":
        return "openInspectCharges";
      case "equipmentRates":
        return "equipmentCharges";
      default:
        return null;
    }
  };

  const entries = useMemo(() => Object.entries(rates || {}), [rates]);
  const filtered = useMemo(() => {
    const lower = search.trim().toLowerCase();
    if (!lower) return entries;
    return entries.filter(([key, rate]) =>
      getBillingLabel(key, rate)
        .toLowerCase()
        .includes(lower)
    );
  }, [entries, search]);

  const toggle = key => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const handleSubmit = () => {
    const sectionKey = mapCategoryToSection(categoryKey);
    if (!sectionKey) {
      onClose();
      return;
    }

    const currentSection = values?.[sectionKey] || {};
    const updatedSection = { ...currentSection };
    Array.from(selected).forEach(rateKey => {
      updatedSection[rateKey] = currentSection[rateKey] || "";
    });

    const nextValues = {
      ...values,
      [sectionKey]: updatedSection,
    };

    setValues(nextValues, false);
    onClose();
  };

  return (
    <Dialog open maxWidth="xs" fullWidth onClose={onClose}>
      <DialogTitle sx={{ position: "relative" }}>
        <Typography fontWeight={500} fontSize={20} color="text.primary" align="center">
          {title} Charges
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            placeholder="Search"
            size="small"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Box
            display="flex"
            flexDirection="column"
            gap={0.5}
            maxHeight={360}
            sx={{ overflowY: "auto" }}
          >
            {filtered.length === 0 && <Typography color="textSecondary">No rates found</Typography>}
            {filtered.map(([key, rate], index) => (
              <React.Fragment key={key}>
                <FormControlLabel
                  control={<Checkbox checked={selected.has(key)} onChange={() => toggle(key)} />}
                  label={getBillingLabel(key, rate)}
                />
                {index < filtered.length - 1 && (
                  <Divider sx={{ borderColor: theme => theme.palette.grey[300] }} />
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={handleSubmit}>
          Add Quantities
        </Button>
      </DialogActions>
    </Dialog>
  );
}
